import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleCheck,
  faCircleExclamation,
  faCode,
  faCopy,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EAdminMercadoPagoQueryType } from "../../../Enum/EAdminMercadoPagoQueryType"
import { AdminMercadoPagoQuery } from "../../../Objects/AdminDashboard/AdminMercadoPagoQuery"
import { AdminMercadoPagoQueryResult } from "../../../Objects/AdminDashboard/AdminMercadoPagoQueryResult"
import { useAdminMercadoPagoQuery } from "../../../hooks/AdminDashboard/useAdminMercadoPagoQuery"
import { useTranslation } from "react-i18next"
import { sanitizeMercadoPagoResourceId } from "../../../utils/mercadoPago"

const formatJson = (value: string) => {
  if (!value.trim()) {
    return ""
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

export const MercadoPagoQueriesSection: React.FC = () => {
  const { t } = useTranslation("frontend")

  const { handleQuery, isQuerying } = useAdminMercadoPagoQuery()

  const [form, setForm] = useState(new AdminMercadoPagoQuery())

  const [result, setResult] = useState<AdminMercadoPagoQueryResult | null>(null)

  const queryTypeOptions = useMemo(
    () => [
      {
        value: String(EAdminMercadoPagoQueryType.Payment),
        label: t("admin_dashboard.mercado_pago_queries.type_payment"),
      },
      {
        value: String(EAdminMercadoPagoQueryType.AuthorizedPayment),
        label: t("admin_dashboard.mercado_pago_queries.type_authorized_payment"),
      },
      {
        value: String(EAdminMercadoPagoQueryType.Preapproval),
        label: t("admin_dashboard.mercado_pago_queries.type_preapproval"),
      },
      {
        value: String(EAdminMercadoPagoQueryType.PreapprovalPlan),
        label: t("admin_dashboard.mercado_pago_queries.type_preapproval_plan"),
      },
      {
        value: String(EAdminMercadoPagoQueryType.Preference),
        label: t("admin_dashboard.mercado_pago_queries.type_preference"),
      },
      {
        value: String(EAdminMercadoPagoQueryType.MerchantOrder),
        label: t("admin_dashboard.mercado_pago_queries.type_merchant_order"),
      },
    ],
    [t],
  )

  const formattedResponse = useMemo(() => formatJson(result?.responseBody ?? ""), [result?.responseBody])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    handleQuery(form, setResult)
  }

  const handleCopy = async () => {
    if (!formattedResponse) {
      return
    }

    await navigator.clipboard.writeText(formattedResponse)
  }

  return (
    <div className="w-full overflow-auto bg-slate-50/60 p-1 md:p-0">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
        <div className="mt-2 mx-1 rounded-lg border border-orange-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="mr-2 text-orange-500" />
                {t("admin_dashboard.mercado_pago_queries.title")}
              </h3>

              <p className="text-[12px] text-slate-500">{t("admin_dashboard.mercado_pago_queries.description")}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[280px,1fr,auto]">
            <GenericDropDown
              data={queryTypeOptions}
              initialState={String(form.queryType)}
              handlerFuncion={(event) =>
                setForm(
                  new AdminMercadoPagoQuery({
                    ...form,
                    queryType: Number(event.target.value) as EAdminMercadoPagoQueryType,
                  }),
                )
              }
              descriptionKey="interactionDescriptions:pages.adminDashboard.sections.mercadoPagoQueriesSection.select.select"
            />

            <InputField
              type="text"
              value={form.resourceId}
              placeholder={t("admin_dashboard.mercado_pago_queries.resource_id")}
              onInvalidMessage={t("admin_dashboard.mercado_pago_queries.resource_id_invalid")}
              icon={faCode}
              required
              maxLength={180}
              pattern="[A-Za-z0-9._:-]+"
              onChange={(event) =>
                setForm(
                  new AdminMercadoPagoQuery({ ...form, resourceId: sanitizeMercadoPagoResourceId(event.target.value) }),
                )
              }
              descriptionKey="interactionDescriptions:pages.adminDashboard.sections.mercadoPagoQueriesSection.input.resourceId"
            />

            <button
              type="submit"
              disabled={isQuerying}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-orange-500 bg-orange-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
              data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.mercadoPagoQueriesSection.button.submit"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              {t("admin_dashboard.mercado_pago_queries.submit")}
            </button>
          </form>
        </div>
      </Animate>

      {result && (
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
          <div className="mx-1 mt-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-1 text-xs text-slate-700">
                <p className="font-extrabold text-slate-900">{result.requestPath}</p>

                <p>
                  {t("admin_dashboard.mercado_pago_queries.captured_at")}:{" "}
                  <span className="font-semibold">
                    {result.capturedAtUtc ? new Date(result.capturedAtUtc).toLocaleString() : "-"}
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                    result.isSuccessStatusCode
                      ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                      : "bg-red-100 text-red-800 ring-1 ring-red-200"
                  }`}
                >
                  <FontAwesomeIcon icon={result.isSuccessStatusCode ? faCircleCheck : faCircleExclamation} />
                  HTTP {result.statusCode}
                </span>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                  onClick={handleCopy}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.mercadoPagoQueriesSection.button.handleCopy"
                >
                  <FontAwesomeIcon icon={faCopy} />
                  {t("admin_dashboard.mercado_pago_queries.copy")}
                </button>
              </div>
            </div>

            <pre className="mt-3 max-h-[70dvh] overflow-auto rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs leading-relaxed text-slate-100">
              {formattedResponse || t("admin_dashboard.mercado_pago_queries.empty_response")}
            </pre>
          </div>
        </Animate>
      )}
    </div>
  )
}
